/**
 * Advanced object-connection search helpers (sort + cell display).
 * Mirrors AddoShare.Client objectConnectionSearchUtils.js behaviour.
 */

function getRowFieldValue(row, fieldName) {
  if (row == null) return undefined;
  const key = String(fieldName ?? "").trim();
  if (!key) return undefined;
  if (row[key] != null) return row[key];
  const match = Object.keys(row).find(
    (k) => k.trim().toLowerCase() === key.toLowerCase(),
  );
  return match ? row[match] : undefined;
}

/** Normalize a grid cell value for display (trim padded backend strings). */
export function resolveAdvSearchCellValue(value) {
  if (value == null) return "";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value).trim();
    }
  }
  return String(value).trim();
}

/** Compare two sort keys; numbers sort numerically, otherwise locale-aware text. */
export function compareSortValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null || resolveAdvSearchCellValue(a) === "") return 1;
  if (b == null || resolveAdvSearchCellValue(b) === "") return -1;

  const sa = resolveAdvSearchCellValue(a);
  const sb = resolveAdvSearchCellValue(b);
  const na = Number(sa);
  const nb = Number(sb);
  const numericPattern = /^-?\d+(\.\d+)?$/;
  const aIsNum = numericPattern.test(sa) && !Number.isNaN(na);
  const bIsNum = numericPattern.test(sb) && !Number.isNaN(nb);

  if (aIsNum && bIsNum) return na - nb;
  return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: "base" });
}

/**
 * Resolve LU key column field names used for key-based sort.
 * Prefers mruKeyNames from the backend; falls back to the first visible column.
 */
export function resolveObjectConnectionSortColumns(mruKeyNames, advObjConColumns) {
  const keys = Array.isArray(mruKeyNames) ? mruKeyNames : [];
  const fromMru = keys
    .map((k) => (k?.key ?? k?.name ?? (typeof k === "string" ? k : "")).trim())
    .filter(Boolean);
  if (fromMru.length > 0) return fromMru;

  const cols = Array.isArray(advObjConColumns) ? advObjConColumns : [];
  const firstField = (cols[0]?.fieldName ?? "").trim();
  return firstField ? [firstField] : [];
}

/** Sort advanced search rows descending by LU key columns (e.g. WO_NO). */
export function sortObjectConnectionResultsByPrimaryColumn(
  rows,
  mruKeyNames,
  advObjConColumns = [],
) {
  const data = Array.isArray(rows) ? rows : [];
  const sortColumns = resolveObjectConnectionSortColumns(
    mruKeyNames,
    advObjConColumns,
  );
  if (data.length <= 1 || sortColumns.length === 0) return data;

  return [...data].sort((rowA, rowB) => {
    for (const fieldName of sortColumns) {
      const cmp = compareSortValues(
        getRowFieldValue(rowA, fieldName),
        getRowFieldValue(rowB, fieldName),
      );
      if (cmp !== 0) return -cmp;
    }
    return 0;
  });
}

function normalizeFieldToken(value) {
  return String(value ?? "").trim().toLowerCase();
}

/**
 * Number of grid columns that correspond to LU key fields (matches AdvSearchObjectFilter).
 */
export function getAdvSearchKeyFieldCount(advObjConColumns, mruKeyNames) {
  const cols = Array.isArray(advObjConColumns) ? advObjConColumns : [];
  const keys = Array.isArray(mruKeyNames) ? mruKeyNames : [];
  return Math.min(cols.length, keys.length);
}

/** True when the column is one of the LU key-field columns (first N columns, N = mruKeyNames.length). */
export function isAdvSearchKeyFieldColumn(column, mruKeyNames, advObjConColumns = []) {
  const cols = Array.isArray(advObjConColumns) ? advObjConColumns : [];
  const keyCount = getAdvSearchKeyFieldCount(cols, mruKeyNames);
  if (keyCount === 0) return false;

  const fieldName = normalizeFieldToken(column?.fieldName);
  if (!fieldName) return false;

  return cols.slice(0, keyCount).some(
    (col) => normalizeFieldToken(col?.fieldName) === fieldName,
  );
}

/** Default sort field for advanced search (primary key column). */
export function getDefaultAdvSearchSortField(mruKeyNames, advObjConColumns = []) {
  const cols = Array.isArray(advObjConColumns) ? advObjConColumns : [];
  const keyCount = getAdvSearchKeyFieldCount(cols, mruKeyNames);
  if (keyCount > 0) {
    return String(cols[0]?.fieldName ?? "").trim();
  }

  const keys = resolveObjectConnectionSortColumns(mruKeyNames, advObjConColumns);
  return keys[0] ?? "";
}

/** Sort advanced search rows by a single column (ascending or descending). */
export function sortAdvSearchItemsByField(items, fieldName, isSortedDescending) {
  const data = Array.isArray(items) ? items : [];
  const field = String(fieldName ?? "").trim();
  if (data.length <= 1 || !field) return data;

  const dir = isSortedDescending ? -1 : 1;
  return [...data].sort((rowA, rowB) => {
    const cmp = compareSortValues(
      getRowFieldValue(rowA, field),
      getRowFieldValue(rowB, field),
    );
    return cmp * dir;
  });
}
